/**
 * Sidebar (rail) — Variant B left navigation.
 *
 * - Dark in BOTH light and dark themes (`bg-rail`) per Variant B handoff.
 * - Width is driven by the parent <AppShell> grid: 248px (lg+), 76px (md), hidden (<md).
 *   Inner labels, group headings, exam card text, and user-row text auto-hide at 76px
 *   via the `lg:inline hidden` pattern (the rail-collapsed view shows icons only).
 * - Active state is resolved by react-router's <NavLink>, not by a `currentPath` prop.
 * - Three nav groups (Learn / Practice / You). Unshipped destinations carry a
 *   `comingSoon` flag and are filtered out entirely; an empty group renders nothing.
 * - Bottom: exam countdown card (per-user, derived from the auth store's
 *   `exam_date`/`exam_center`; falls back to a "plan your exam" CTA) + user row
 *   with a click-to-toggle dropdown (Profile / Billing / Language / Sign out).
 */
import { useState, useRef, useEffect, type CSSProperties } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { computeExamCountdown, examCardState, formatExamDate } from '../../utils/examCountdown';
import { useIsPremium } from '../../stores/premiumStore';
import { useCertStore } from '../../stores/certStore';
import { useUIStore } from '../../stores/uiStore';
import LanguageSwitcher from '../LanguageSwitcher';

/* ── Stroke icons (lucide-style, 24-grid) ───────────────────────────────── */

const ICON_PATHS = {
  home: 'M3 11.5 12 4l9 7.5V20a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1v-8.5Z',
  compass: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM16 8l-2 6-6 2 2-6 6-2Z',
  route: 'M9 19a4 4 0 0 1-4-4V7a4 4 0 0 1 8 0v10a4 4 0 0 0 8 0V9',
  graduation: 'M22 10 12 5 2 10l10 5 10-5ZM6 12v5a6 6 0 0 0 12 0v-5',
  bookopen: 'M2 4h7a3 3 0 0 1 3 3v13a2 2 0 0 0-2-2H2V4ZM22 4h-7a3 3 0 0 0-3 3v13a2 2 0 0 1 2-2h8V4Z',
  dumbbell: 'M6 6v12M2 9v6M22 9v6M18 6v12M6 12h12',
  filecheck: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6ZM14 3v6h6M9 15l2 2 4-4',
  bookmark: 'M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16Z',
  barchart: 'M3 3v18h18M7 16v-7M12 16V8M17 16v-4',
  flame: 'M12 2c1 3-1 5-2 6-1.2 1.2-2 2.6-2 4.3A4 4 0 0 0 16 14c0-1-.3-2-1-3 1.7.5 3 2.3 3 4.3a6 6 0 0 1-12 0c0-3.3 2.5-5.6 4-7.6C11 6 12 4 12 2Z',
  calendar: 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z',
  chevron: 'M6 9l6 6 6-6',
  chevronsLeft: 'M11 17l-5-5 5-5M18 17l-5-5 5-5',
  chevronsRight: 'M13 17l5-5-5-5M6 17l5-5-5-5',
} as const;

type IconName = keyof typeof ICON_PATHS;

function Icon({ name, size = 18, className = '' }: { name: IconName; size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d={ICON_PATHS[name]} />
    </svg>
  );
}

/* ── Nav config ──────────────────────────────────────────────────────────── */

interface NavItem {
  icon: IconName;
  labelKey: string;
  to: string;
  /** End-match for NavLink (used for `/dashboard` so it doesn't stay active on subroutes). */
  end?: boolean;
  /** Feature not built yet — rendered as a non-clickable row with a "Soon" tag. */
  comingSoon?: boolean;
}

interface NavGroup {
  labelKey: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    labelKey: 'nav.groupLearn',
    items: [
      { icon: 'home', labelKey: 'nav.dashboard', to: '/dashboard', end: true },
      { icon: 'compass', labelKey: 'nav.browse', to: '/browse', comingSoon: true },
      { icon: 'route', labelKey: 'nav.learningPaths', to: '/learning-paths', comingSoon: true },
      { icon: 'graduation', labelKey: 'nav.skillIQ', to: '/skill-iq', comingSoon: true },
    ],
  },
  {
    labelKey: 'nav.groupPractice',
    items: [
      { icon: 'bookopen', labelKey: 'nav.studyMode', to: '/study' },
      { icon: 'dumbbell', labelKey: 'nav.drills', to: '/drills', comingSoon: true },
      { icon: 'filecheck', labelKey: 'nav.mockExams', to: '/exam' },
    ],
  },
  {
    labelKey: 'nav.groupYou',
    items: [
      { icon: 'bookmark', labelKey: 'nav.flagged', to: '/bookmarks' },
      { icon: 'barchart', labelKey: 'nav.progress', to: '/profile' },
    ],
  },
];

/* ── Props ──────────────────────────────────────────────────────────────── */

/** Populated exam-card data (already computed + locale-formatted). */
export interface SidebarExam {
  daysLeft: number;
  pct: number;
  /** Preformatted, locale-aware display date. */
  date: string;
  /** Exam center name, or null when unset. */
  center: string | null;
}

export interface SidebarProps {
  /**
   * Test/storybook override for the exam countdown card. When omitted (the
   * default) the card is derived from the authenticated user's
   * `exam_date`/`exam_center`. Pass `null` to force the empty-state CTA.
   */
  exam?: SidebarExam | null;
}

/* ── Component ──────────────────────────────────────────────────────────── */

export function Sidebar({ exam }: SidebarProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuthStore();
  const isPremium = useIsPremium();
  const certCode = useCertStore((s) => s.certCode);
  const providerName = useCertStore((s) => s.providerName);
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);

  /* Label-visibility helpers.
     By default, labels appear only at `lg+` (`hidden lg:…`); at `md–lg` the
     rail is icon-only. When the user manually collapses at `lg+`, we force the
     labelled elements off (`hidden`) and reveal the compact ones so the rail
     matches the 76px icon layout. `expanded*` = show label; `compact` = show
     the icon-only fallback (e.g. the exam card's day-count-only view). */
  const expandedInline = collapsed ? 'hidden' : 'hidden lg:inline';
  const expandedBlock = collapsed ? 'hidden' : 'hidden lg:block';
  const expandedFlex = collapsed ? 'hidden' : 'hidden lg:flex';
  const expandedInlineBlock = collapsed ? 'hidden' : 'hidden lg:inline-block';
  const compactHideAtLg = collapsed ? '' : 'lg:hidden';

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  /* Close dropdown on outside click */
  useEffect(() => {
    if (!userMenuOpen) return;
    function handle(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [userMenuOpen]);

  /* Close on Escape */
  useEffect(() => {
    if (!userMenuOpen) return;
    function handle(e: KeyboardEvent) {
      if (e.key === 'Escape') setUserMenuOpen(false);
    }
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [userMenuOpen]);

  const displayName = user?.display_name || user?.username || t('common.username');
  const initials = (displayName ?? 'U')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const planLabel = isPremium
    ? t('header.premiumBadge')
    : t('sidebar.userPlan.proTrial', { days: 9, defaultValue: 'Pro trial · 9 days left' });

  /* Exam card has three states, from the user's stored date:
       'upcoming' → populated countdown card
       'lapsed'   → date exists but is past → "reschedule" state
       'none'     → no date → generic "plan your exam" CTA
     A fixed `exam` prop overrides store derivation (object → populated card,
     `null` → the generic CTA); it never forces the lapsed state. */
  const dateLocale = i18n.language === 'fr' ? 'fr-FR' : 'en-US';
  const now = new Date();
  const storeState = examCardState(user?.exam_date, now);
  const examData: SidebarExam | null =
    exam !== undefined
      ? exam
      : storeState === 'upcoming' && user?.exam_date
        ? {
            ...computeExamCountdown(user.exam_date, now),
            date: formatExamDate(user.exam_date, dateLocale),
            center: user.exam_center ?? null,
          }
        : null;
  // Distinct lapsed state (store-derived only; the `exam` prop never lapses).
  const showLapsed = exam === undefined && storeState === 'lapsed';

  const navItemClass = ({ isActive }: { isActive: boolean }) =>
    [
      'relative flex items-center gap-3 rounded-[10px] px-3 py-2 transition-colors',
      'text-[13.5px] font-semibold',
      isActive
        ? 'bg-rail-active text-white'
        : 'text-rail-ink-2 hover:bg-white/[0.04] hover:text-rail-ink',
    ].join(' ');

  const examGradient: CSSProperties = {
    background: 'linear-gradient(150deg, #1D4ED8, #142B66)',
  };
  const examGlow: CSSProperties = {
    background: 'radial-gradient(circle at 78% 18%, rgba(245,158,11,0.32), rgba(245,158,11,0) 60%)',
  };

  return (
    <aside
      className="bg-rail text-rail-ink flex h-full flex-col overflow-y-auto px-3 py-4"
      aria-label={t('sidebar.label', { defaultValue: 'Main navigation' })}
    >
      {/* ── Brand + collapse toggle ─────────────────────────────────────── */}
      {/* When collapsed the 76px rail can't fit brand + button side by side, so
         they stack (logo on top, toggle below). The toggle itself only shows at
         `lg+` — below that the rail width is breakpoint-driven, not user-driven. */}
      <div className={`mb-6 flex px-1 ${collapsed ? 'flex-col items-center gap-3' : 'items-center justify-between'}`}>
        <NavLink to="/dashboard" className="flex items-center gap-2.5 outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded-lg">
          <svg width="34" height="34" viewBox="0 0 32 32" fill="none" aria-hidden="true" className="flex-shrink-0">
            <rect x="0.5" y="0.5" width="31" height="31" rx="8" fill="#16213A" stroke="rgba(255,255,255,.1)" />
            <path d="M9 11.5C9 9.567 10.567 8 12.5 8H22V11H13C12.4477 11 12 11.4477 12 12V20C12 20.5523 12.4477 21 13 21H22V24H12.5C10.567 24 9 22.433 9 20.5V11.5Z" fill="#fff" />
            <path d="M19 13L16 18H19L17.5 24L23 16.5H20L22 13H19Z" fill="#F59E0B" />
          </svg>
          <div className={`${expandedFlex} flex-col leading-tight`}>
            <span className="text-[15px] font-extrabold tracking-tight text-rail-ink">
              Cloud<span className="text-blue-400">Master</span>
            </span>
            <span className="mt-0.5 text-[9.5px] font-extrabold uppercase tracking-[0.13em] text-rail-ink-2">
              {certCode?.toUpperCase()}{providerName ? ` · ${providerName}` : ''}
            </span>
          </div>
        </NavLink>
        <button
          type="button"
          onClick={toggleSidebar}
          className="hidden lg:flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-rail-ink-2 transition-colors hover:bg-white/[0.04] hover:text-rail-ink outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          aria-pressed={collapsed}
          aria-label={
            collapsed
              ? t('sidebar.expand', { defaultValue: 'Expand sidebar' })
              : t('sidebar.collapse', { defaultValue: 'Collapse sidebar' })
          }
          title={
            collapsed
              ? t('sidebar.expand', { defaultValue: 'Expand sidebar' })
              : t('sidebar.collapse', { defaultValue: 'Collapse sidebar' })
          }
        >
          <Icon name={collapsed ? 'chevronsRight' : 'chevronsLeft'} size={18} />
        </button>
      </div>

      {/* ── Nav groups ──────────────────────────────────────────────────── */}
      {/* `comingSoon` items are unbuilt destinations — filtered out entirely
         (single source: the flag on NAV_GROUPS). A group with no shipped items
         renders nothing, header included. */}
      {NAV_GROUPS.map((group) => {
        const items = group.items.filter((item) => !item.comingSoon);
        if (items.length === 0) return null;
        return (
          <div key={group.labelKey} className="mb-4">
            <div className={`${expandedBlock} mb-1.5 px-3 text-[9.5px] font-extrabold uppercase tracking-[0.13em] text-rail-ink-2`}>
              {t(group.labelKey)}
            </div>
            <nav className="flex flex-col gap-0.5" aria-label={t(group.labelKey)}>
              {items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={navItemClass}
                >
                  {({ isActive }) => (
                    <>
                      {/* Active 3px left rail */}
                      {isActive && (
                        <span
                          aria-hidden="true"
                          className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-blue-400"
                        />
                      )}
                      <Icon name={item.icon} size={18} className="flex-shrink-0" />
                      <span className={`${expandedInline} flex-1 truncate`}>{t(item.labelKey)}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
          </div>
        );
      })}

      {/* ── Spacer — pushes exam card + user row to the bottom ───────────── */}
      <div className="flex-1" />

      {/* ── Exam countdown card ─────────────────────────────────────────── */}
      {/* Three states: populated countdown (upcoming), a "lapsed" reschedule
          prompt (past date), or the generic "plan your exam" CTA (no date).
          Each keeps the expanded + compact (76px rail) variants; both CTAs
          point at /profile (where the date is configured). */}
      <div
        className="relative mb-3 overflow-hidden rounded-2xl p-4 text-white"
        style={examGradient}
      >
        <span aria-hidden="true" className="pointer-events-none absolute inset-0" style={examGlow} />

        {examData ? (
          <>
            <div className={`${expandedBlock} relative`}>
              <div className="text-[10px] font-extrabold uppercase tracking-[0.13em] text-blue-200">
                {t('sidebar.examIn', { defaultValue: 'Exam in' })}
              </div>
              <div className="mt-1 flex items-baseline gap-1.5">
                <span
                  className="font-mono text-[28px] font-extrabold tabular-nums leading-none"
                  style={{ fontFeatureSettings: '"tnum"' }}
                >
                  {examData.daysLeft}
                </span>
                <span className="text-[12px] font-semibold text-blue-100">
                  {t('common.days')}
                </span>
              </div>
              <div
                className="relative mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/15"
                role="progressbar"
                aria-valuenow={Math.round(examData.pct)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t('sidebar.examProgress', { defaultValue: 'Exam preparation progress' })}
              >
                <div
                  className="h-full rounded-full bg-amber-400 transition-[width] duration-700 ease-out"
                  style={{ width: `${examData.pct}%` }}
                />
              </div>
              <div className="mt-2 text-[11px] text-blue-100/90">
                {examData.center ? `${examData.date} · ${examData.center}` : examData.date}
              </div>
            </div>
            {/* Compact (76px rail) — day count only. Shown at md–lg always, and
                at lg+ when the user has collapsed the rail. */}
            <div className={`${compactHideAtLg} relative flex flex-col items-center text-center`}>
              <span
                className="font-mono text-xl font-extrabold tabular-nums leading-none"
                style={{ fontFeatureSettings: '"tnum"' }}
              >
                {examData.daysLeft}
              </span>
              <span className="mt-0.5 text-[9px] font-extrabold uppercase tracking-[0.13em] text-blue-200">
                {t('common.days')}
              </span>
            </div>
          </>
        ) : showLapsed ? (
          <>
            {/* Lapsed state — exam date is past; prompt to schedule the next. */}
            <div className={`${expandedBlock} relative`}>
              <div className="flex items-center gap-2 text-blue-100">
                <Icon name="calendar" size={18} className="flex-shrink-0" />
                <span className="text-[12.5px] font-semibold leading-snug">
                  {t('sidebar.examLapsed', { defaultValue: 'Your exam has passed' })}
                </span>
              </div>
              <button
                type="button"
                onClick={() => navigate('/profile')}
                className="mt-3 w-full rounded-lg bg-white/15 px-3 py-1.5 text-[12px] font-bold text-white transition-colors hover:bg-white/25 outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
              >
                {t('sidebar.examLapsedButton', { defaultValue: 'Set your reminder for the next exam' })}
              </button>
            </div>
            {/* Compact — a single calendar glyph linking to /profile. */}
            <button
              type="button"
              onClick={() => navigate('/profile')}
              aria-label={t('sidebar.examLapsed', { defaultValue: 'Your exam has passed' })}
              className={`${compactHideAtLg} relative mx-auto flex items-center justify-center rounded-lg text-blue-100 transition-colors hover:text-white outline-none focus-visible:ring-2 focus-visible:ring-blue-300`}
            >
              <Icon name="calendar" size={20} />
            </button>
          </>
        ) : (
          <>
            {/* Empty state — plan-your-exam CTA (expanded). */}
            <div className={`${expandedBlock} relative`}>
              <div className="flex items-center gap-2 text-blue-100">
                <Icon name="calendar" size={18} className="flex-shrink-0" />
                <span className="text-[12.5px] font-semibold leading-snug">
                  {t('sidebar.examCta', { defaultValue: 'Schedule your exam' })}
                </span>
              </div>
              <button
                type="button"
                onClick={() => navigate('/profile')}
                className="mt-3 w-full rounded-lg bg-white/15 px-3 py-1.5 text-[12px] font-bold text-white transition-colors hover:bg-white/25 outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
              >
                {t('sidebar.examCtaButton', { defaultValue: 'Set a date' })}
              </button>
            </div>
            {/* Compact — a single calendar glyph linking to /profile. */}
            <button
              type="button"
              onClick={() => navigate('/profile')}
              aria-label={t('sidebar.examCta', { defaultValue: 'Schedule your exam' })}
              className={`${compactHideAtLg} relative mx-auto flex items-center justify-center rounded-lg text-blue-100 transition-colors hover:text-white outline-none focus-visible:ring-2 focus-visible:ring-blue-300`}
            >
              <Icon name="calendar" size={20} />
            </button>
          </>
        )}
      </div>

      {/* ── User row + dropdown ─────────────────────────────────────────── */}
      <div className="relative" ref={userMenuRef}>
        <button
          type="button"
          onClick={() => setUserMenuOpen((o) => !o)}
          className="flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left transition-colors hover:bg-white/[0.04] outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          aria-haspopup="menu"
          aria-expanded={userMenuOpen}
          aria-label={t('header.userMenu')}
        >
          <span
            className="flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-violet-500 text-[12px] font-bold text-white"
            aria-hidden="true"
          >
            {initials}
          </span>
          <span className={`${expandedFlex} min-w-0 flex-1 flex-col leading-tight`}>
            <span className="truncate text-[13px] font-bold text-rail-ink">{displayName}</span>
            <span className="truncate text-[11px] text-rail-ink-2">{planLabel}</span>
          </span>
          <Icon name="chevron" size={14} className={`${expandedInlineBlock} flex-shrink-0 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
        </button>

        {userMenuOpen && isAuthenticated && (
          <div
            role="menu"
            className={`absolute bottom-full mb-2 rounded-xl border border-white/10 bg-[#16213A] py-1.5 shadow-2xl ${
              collapsed ? 'left-0 w-56' : 'left-0 right-0'
            }`}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => { setUserMenuOpen(false); navigate('/profile'); }}
              className="w-full px-3 py-2 text-left text-[13px] font-medium text-rail-ink hover:bg-white/[0.06]"
            >
              {t('header.profile')}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => { setUserMenuOpen(false); navigate('/billing'); }}
              className="w-full px-3 py-2 text-left text-[13px] font-medium text-rail-ink hover:bg-white/[0.06]"
            >
              {t('header.billing')}
            </button>
            <div className="px-3 py-1">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-rail-ink-2">
                {t('common.language')}
              </span>
              <div className="mt-1 [&_button]:!text-rail-ink [&_button]:!bg-transparent">
                <LanguageSwitcher variant="mobile" />
              </div>
            </div>
            <div className="my-1 border-t border-white/10" />
            <button
              type="button"
              role="menuitem"
              onClick={() => { setUserMenuOpen(false); logout(); navigate('/login'); }}
              className="w-full px-3 py-2 text-left text-[13px] font-medium text-red-300 hover:bg-red-500/10"
            >
              {t('header.signOut')}
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
